import {
  startTransition,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
  type TransitionEvent,
} from 'react';

/**
 * How long the grid's `grid-template-columns` transition runs. Kept in sync
 * with the `duration-300` on {@link ListDetailSplit}'s grid.
 */
const COLLAPSE_MS = 300;

/**
 * When the collapse fallback fires, if `transitionend` never did. A margin
 * over {@link COLLAPSE_MS} so the event wins the race in the normal case and
 * the pane isn't yanked out mid-animation.
 */
const COLLAPSE_FALLBACK_MS = COLLAPSE_MS + 100;

/** Tracks for the three states the grid can be in. */
function resolveGridColumns(
  isOpen: boolean,
  railCollapsed: boolean,
  openColumns: string,
): string {
  if (!isOpen) return '1fr 0fr';
  return railCollapsed ? '13rem 1fr' : openColumns;
}

/**
 * Whether an event is the grid's own column sweep finishing, rather than one
 * bubbling up from a descendant or a different animated property.
 */
function isTracksTransition(event: TransitionEvent<HTMLDivElement>): boolean {
  return (
    event.target === event.currentTarget &&
    event.propertyName === 'grid-template-columns'
  );
}

export interface ListDetailSplitController {
  /** The row the user picked; null when the full table is showing. */
  selectedId: string | null;
  /**
   * The row the detail pane is rendering. Tracks {@link selectedId}, but lags
   * it in both directions: it is set at lower priority on open, and lingers
   * through the collapse animation on close.
   */
  detailId: string | null;
  isOpen: boolean;
  railCollapsed: boolean;
  setRailCollapsed: (next: boolean | ((prev: boolean) => boolean)) => void;
  /** `grid-template-columns` for the current state. */
  gridColumns: string;
  railScrollRef: RefObject<HTMLDivElement | null>;
  selectedRowRef: RefObject<HTMLLIElement | null>;
  listPaneRef: RefObject<HTMLDivElement | null>;
  select: (id: string) => void;
  close: () => void;
  onTracksTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => void;
}

export interface UseListDetailSplitOptions {
  /** The selected id as the URL currently states it; null when none. */
  urlSelectedId: string | null;
  /**
   * Write the selection back to the URL — `undefined` on close. Called after
   * the local state has already flipped, so the navigation never blocks the
   * layout change.
   */
  onSelectionChange: (id: string | undefined) => void;
  /** Tracks while open with the rail expanded. More right = wider detail. */
  openColumns?: string;
  /** Whether the list is still loading, so the scroll-into-view can re-run. */
  isLoading?: boolean;
}

/**
 * The selection and animation state behind a list ↔ detail split view: the
 * full table collapses to a narrow rail on the left while the detail expands
 * in from the right, on an animated `grid-template-columns` sweep.
 *
 * Selection is held locally rather than read straight off the URL because the
 * navigation that records it is async (route `beforeLoad` guards), and waiting
 * on it would stall the layout flip. The URL stays the shareable source of
 * truth: an external change to it (back/forward, deep link) re-syncs here via
 * the setState-during-render pattern.
 *
 * Pair with {@link ListDetailSplit}, which renders the tracks and wires the
 * refs this returns.
 */
export function useListDetailSplit({
  urlSelectedId,
  onSelectionChange,
  openColumns = '1fr 2fr',
  isLoading = false,
}: UseListDetailSplitOptions): ListDetailSplitController {
  const [selectedId, setSelectedId] = useState<string | null>(urlSelectedId);
  const [detailId, setDetailId] = useState<string | null>(urlSelectedId);
  const [lastUrlId, setLastUrlId] = useState<string | null>(urlSelectedId);
  if (urlSelectedId !== lastUrlId) {
    setLastUrlId(urlSelectedId);
    setSelectedId(urlSelectedId);
    if (urlSelectedId !== null) setDetailId(urlSelectedId);
  }
  const isOpen = selectedId !== null;

  // Collapse the left rail to a narrow strip while reading, handing the width
  // to the detail pane. Only meaningful while open — the closed state shows
  // the full table regardless.
  const [railCollapsed, setRailCollapsed] = useState(false);

  const gridColumns = resolveGridColumns(isOpen, railCollapsed, openColumns);

  // Drop the detail once the collapse has had time to finish. `transitionend`
  // normally gets there first, but it never fires when the browser skips the
  // transition (a background tab, a hidden element, reduced motion) — and
  // without this the detail would stay mounted, with its queries live, inside
  // a zero-width track.
  //
  // Reacting to state rather than arming the timer inside `close()` is what
  // makes the URL-driven paths safe too: back/forward closing the pane arms
  // the fallback the same way, and back/forward opening a *different* row
  // flips `isOpen` back on, so the cleanup cancels a timer that would
  // otherwise unmount the row that was just opened.
  useEffect(() => {
    if (isOpen || detailId === null) return;
    const timer = setTimeout(() => setDetailId(null), COLLAPSE_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [isOpen, detailId]);

  const railRefs = useKeepSelectedRowVisible({
    isOpen,
    detailId,
    selectedId,
    railCollapsed,
    isLoading,
  });
  const listPaneRef = useRestoreFocusOnClose({ isOpen, detailId });

  // Plain functions, not useCallback: React Compiler is on repo-wide and
  // caches them, and nothing here reads them from a dependency array.
  const select = (id: string) => {
    // Flip the layout urgently so the grid starts animating on this commit…
    setSelectedId(id);
    // …but mount the (potentially heavy) detail subtree at lower priority, so
    // React paints the first animation frame before rendering it. Without this
    // the synchronous mount blocks paint and the expand visibly hitches.
    startTransition(() => setDetailId(id));
    onSelectionChange(id);
  };

  const close = () => {
    // Leave detailId set so the pane stays mounted while the track collapses —
    // it should slide out, not vanish before the animation finishes. The
    // effect above drops it once the collapse has had time to run.
    setSelectedId(null);
    onSelectionChange(undefined);
  };

  const onTracksTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (isOpen || !isTracksTransition(event)) return;
    setDetailId(null);
  };

  return {
    selectedId,
    detailId,
    isOpen,
    railCollapsed,
    setRailCollapsed,
    gridColumns,
    railScrollRef: railRefs.railScrollRef,
    selectedRowRef: railRefs.selectedRowRef,
    listPaneRef,
    select,
    close,
    onTracksTransitionEnd,
  };
}

/**
 * Keep the selected row visible after the table→rail swap. The rail is a fresh
 * scroll container (starts at the top), so without this the list appears to
 * jump to the top when a row opens. Scrolls before paint, container-scoped
 * "nearest" so an already-visible selection stays put.
 *
 * Owns the refs rather than taking them, so the DOM writes below aren't
 * mutations of a hook argument (which React Compiler refuses to optimize).
 */
function useKeepSelectedRowVisible({
  isOpen,
  detailId,
  selectedId,
  railCollapsed,
  isLoading,
}: {
  isOpen: boolean;
  detailId: string | null;
  selectedId: string | null;
  railCollapsed: boolean;
  isLoading: boolean;
}) {
  const railScrollRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLLIElement>(null);

  useLayoutEffect(() => {
    if (!isOpen || !detailId) return;
    const container = railScrollRef.current;
    const row = selectedRowRef.current;
    if (!container || !row) return;
    const cr = container.getBoundingClientRect();
    const rr = row.getBoundingClientRect();
    if (rr.top < cr.top) {
      container.scrollTop += rr.top - cr.top;
    } else if (rr.bottom > cr.bottom) {
      container.scrollTop += rr.bottom - cr.bottom;
    }
  }, [isOpen, detailId, selectedId, railCollapsed, isLoading]);

  return { railScrollRef, selectedRowRef };
}

/**
 * Mirror of {@link useKeepSelectedRowVisible} for the rail→table swap: closing
 * remounts the full table as a fresh scroll container, which would snap the
 * list back to the top — and since whatever closed it (the rail-header expand
 * button, the detail pane's ✕) unmounts with the pane, keyboard focus would
 * land on `<body>`. Brings the just-closed row back into view and moves focus
 * onto it, falling back to the list pane when the row is gone (paged away).
 */
function useRestoreFocusOnClose({
  isOpen,
  detailId,
}: {
  isOpen: boolean;
  detailId: string | null;
}) {
  const listPaneRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(isOpen);

  useLayoutEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!wasOpen || isOpen) return;
    const pane = listPaneRef.current;
    if (!pane) return;
    const row = detailId
      ? pane.querySelector<HTMLElement>(
          `[data-row-id="${CSS.escape(detailId)}"]`,
        )
      : null;
    if (row) row.scrollIntoView({ block: 'nearest' });
    const target = row ?? pane;
    target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }, [isOpen, detailId]);

  return listPaneRef;
}
